import { useEffect, useRef } from "react";
import { createSearchParams, useNavigate } from "react-router";

import Box from "ds/components/Box";
import Typography from "ds/components/Typography";
import { Policy } from "types/generated";
import Timestamp from "components/Timestamp";
import ListEntitiesItem from "components/ListEntitiesItem";
import { withTestId } from "utils/withTestId";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import EvaluationRecordStatusBadge from "views/Account/PolicySimulation/EvaluationRecordStatusBadge";
import { BADGE_STATES } from "views/Account/PolicySimulation/constants";
import { EvaluationRecordOutcomeStatus } from "views/Account/PolicySimulation/types";
import Link from "ds/components/Link";

import { showPolicyDetailsSamplesDrawer } from "./DetailsDrawer";
import { COLUMN_GAP, COLUMN_ORDER, POLICIES_LIST_ITEM_TEST_ID } from "./constants";

type PolicyDetailsSamplesListItemProps = {
  setRowHeight?: (size: number) => void;
  outcome: string;
  policyId: string;
} & Pick<Policy, "id" | "createdAt">;

const PolicyDetailsSamplesListItem = ({
  id,
  createdAt,
  setRowHeight,
  outcome,
  policyId,
}: PolicyDetailsSamplesListItemProps) => {
  const navigate = useNavigate();
  const rowRef = useRef<HTMLDivElement>(null);

  const handleRowHeight = () => {
    if (setRowHeight && rowRef.current) {
      setRowHeight(rowRef.current.getBoundingClientRect().height);
    }
  };

  // on every render, update the row height
  useEffect(handleRowHeight);

  const handleShowDrawer = () => {
    showPolicyDetailsSamplesDrawer({
      outcome,
      evaluationKey: id,
      timestamp: createdAt,
      policyId,
    });
  };

  const handleSimulationRedirect = () => {
    // redirect to the policy simulation page with the selected evaluation key
    navigate({
      pathname: `/policy/${policyId}`,
      search: createSearchParams({
        evaluationKey: id,
      }).toString(),
    });
  };

  return (
    <ListEntitiesItem
      ref={rowRef}
      direction="row"
      align="center"
      justify="between"
      grid
      gridTemplate={COLUMN_ORDER}
      gap={`0 ${COLUMN_GAP}`}
      {...withTestId(POLICIES_LIST_ITEM_TEST_ID)}
    >
      <Box direction="row" align="center" shrink="0">
        <Typography tag="span" variant="p-body2">
          <Link onPress={handleShowDrawer} variant="secondary">
            <Timestamp timestamp={createdAt} />
          </Link>
        </Typography>
      </Box>
      <Box>
        <EvaluationRecordStatusBadge
          status={BADGE_STATES[outcome] || EvaluationRecordOutcomeStatus.Info}
          text={outcome.toLocaleUpperCase()}
        />
      </Box>

      <DropdownMenuEllipsis tooltip="Policy actions">
        <DropdownMenuItem onAction={handleShowDrawer}>Show details</DropdownMenuItem>
        <DropdownMenuItem onAction={handleSimulationRedirect}>Use to simulate</DropdownMenuItem>
      </DropdownMenuEllipsis>
    </ListEntitiesItem>
  );
};

export default PolicyDetailsSamplesListItem;
